import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnChanges, SimpleChanges,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DatePipe} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {TranslatePipe} from '@ngx-translate/core';
import {catchError, concatMap, finalize, map, Observable, of, Subject, throwError} from 'rxjs';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {MatDatepicker, MatDatepickerInput} from '@angular/material/datepicker';
import moment, {Moment} from 'moment';
import {BenchOverviewService} from '../../../services/bench-overview.service';
import {BenchEntryDTO, BenchEntryPatch, NoteDTO} from '../../../shared/model/bench-entry.model';
import {MessageService} from '../../../shared/components/message.service';
import {DateService} from '../../../services/date.service';
import {INPUT_LIMITS} from '../../../shared/constants/input-limits';
import {LazyImageDirective} from '../../../shared/lazy-image.directive';
import {notesConflict, notesPayload} from '../bench-detail/bench-detail-section-save';

const SAVED_HINT_MS = 1800;

export type InlineField = 'availableFrom' | 'nextStep';

interface NoteJob {
  kind: 'note';
  base: NoteDTO[];
  local: NoteDTO[];
  onSuccess: () => void;
  onError: () => void;
}

type PersistJob =
  | {
      kind: 'patch';
      patch: BenchEntryPatch;
      onSuccess?: (updated: BenchEntryDTO, sent: BenchEntryPatch) => void;
      onError?: (sent: BenchEntryPatch) => void;
    }
  | NoteJob;

interface PersistResult {
  updated: BenchEntryDTO;
  conflict: boolean;
}

const emptyToNull = (v: string | null | undefined): string | null => (v ? v : null);

const copyNotes = (notes: NoteDTO[]): NoteDTO[] => JSON.parse(JSON.stringify(notes)) as NoteDTO[];

/** Normalise empty date strings to null (BE LocalDate can't parse ''). */
const withDatesNormalized = (payload: BenchEntryDTO): BenchEntryDTO => {
  payload.availableFrom = emptyToNull(payload.availableFrom);
  payload.commitmentFrom = emptyToNull(payload.commitmentFrom);
  payload.offers.forEach(o => (o.date = emptyToNull(o.date) as string));
  return payload;
};

/**
 * One consultant/applicant in the meeting. Inline fields (available-from / next-step) persist
 * directly via PATCH with only the changed field — no save button. The "current state" meeting
 * note has its own draft + dirty indicator + explicit save via PUT.
 */
@Component({
  selector: 'app-meeting-row',
  imports: [
    DatePipe,
    FormsModule,
    TranslatePipe,
    MatIcon,
    MatButton,
    MatTooltip,
    MatDatepicker,
    MatDatepickerInput,
    LazyImageDirective,
  ],
  templateUrl: './meeting-row.component.html',
  styleUrl: './meeting-row.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingRowComponent implements OnChanges {
  protected get LIMITS() { return INPUT_LIMITS; }
  private readonly benchService = inject(BenchOverviewService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messageService = inject(MessageService);
  private readonly dateService = inject(DateService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input({required: true}) entry!: BenchEntryDTO;
  @Input() today = '';

  showSaved = false;
  savedField: InlineField | null = null;
  standDraft = '';

  private savedNote: NoteDTO | null = null;
  private savedTimeout: ReturnType<typeof setTimeout> | null = null;
  private inlineSavedTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly momentCache = new Map<string, Moment>();

  // Zuletzt vom Server bestaetigter Stand: Rueckfallwert, wenn ein PATCH abgewiesen wird.
  private serverState: Record<InlineField, string | null> = {availableFrom: null, nextStep: null};

  // Zuletzt eingereihter Stand: Vergleichswert gegen ueberfluessige PATCHes. Haengt am Absenden,
  // nicht an der Antwort, damit ein Zurueckaendern auf den Serverwert noch ein PATCH ausloest.
  private queuedState: Record<InlineField, string | null> = {availableFrom: null, nextStep: null};

  // Serialise inline PATCHes and note PUTs so responses can't apply out of order; the counter
  // keeps the note Save disabled until every queued write settles.
  private inFlight = 0;
  private readonly persistQueue = new Subject<PersistJob>();

  constructor() {
    this.persistQueue
      .pipe(
        concatMap(job => {
          const request: Observable<PersistResult> = job.kind === 'patch'
            ? this.benchService.patchEntry(this.entry.id, job.patch).pipe(map(updated => ({updated, conflict: false})))
            : this.mergeNote(job);
          return request.pipe(
            map(result => ({job, result})),
            catchError(() => of({job, result: null})),
            finalize(() => {
              this.inFlight--;
              this.cdr.markForCheck();
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({job, result}) => {
        if (!result) {
          this.messageService.error('benchDetail.saveError');
          if (job.kind === 'patch') job.onError?.(job.patch);
          else job.onError();
        } else if (job.kind === 'patch') {
          job.onSuccess?.(result.updated, job.patch);
        } else {
          this.adoptNotes(result.updated);
          if (result.conflict) this.messageService.error('meetingMode.note.conflict');
          else job.onSuccess();
        }
        this.cdr.markForCheck();
      });

    this.destroyRef.onDestroy(() => {
      if (this.savedTimeout !== null) clearTimeout(this.savedTimeout);
      if (this.inlineSavedTimeout !== null) clearTimeout(this.inlineSavedTimeout);
    });
  }

  get isSaving(): boolean {
    return this.inFlight > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['entry'] || changes['today']) {
      this.momentCache.clear();
      this.captureServerState();
      this.adoptTodaysNote();
    }
  }

  /** After (re)load, re-attach to today's meeting note so re-saving edits it instead of duplicating. */
  private adoptTodaysNote(): void {
    this.savedNote = this.findTodaysNote();
    this.standDraft = this.savedNote?.text ?? '';
  }

  // Match today's meeting note by date only. The author is stamped server-side
  // (auto-author) and is unknown until the response arrives, so keying on it would
  // miss the note after a reload and let a duplicate be created.
  private findTodaysNote(): NoteDTO | null {
    return this.entry.notes.find(n => n.isMeetingNote && n.date === this.today) ?? null;
  }

  // --- header meta ----------------------------------------------------------

  getInitials(fullName: string): string {
    if (!fullName) return '';
    return fullName.trim().split(/\s+/).map(w => w[0] ?? '').join('').toUpperCase().slice(0, 2);
  }

  /** Offers still in play — open or awaiting feedback. */
  get openOffersCount(): number {
    return this.entry.offers.filter(o => o.status === 'OPEN' || o.status === 'FEEDBACK').length;
  }

  get totalOffers(): number {
    return this.entry.offers.length;
  }

  // --- inline edits (direct PATCH, no save button) --------------------------

  saveInline(field: InlineField): void {
    const value = emptyToNull(this.entry[field]);
    if (value === this.queuedState[field]) return;
    this.queuedState[field] = value;

    this.enqueue({
      kind: 'patch',
      patch: {[field]: value},
      onSuccess: (updated, sent) => this.adoptInlineResult(field, updated, sent),
      onError: sent => this.revertInline(field, sent),
    });
  }

  onDateChange(value: Moment | null): void {
    this.entry.availableFrom = this.dateService.toLocalDateString(value);
    this.saveInline('availableFrom');
  }

  clearDate(): void {
    this.onDateChange(null);
  }

  // --- today's meeting note -------------------------------------------------

  get noteDirty(): boolean {
    return this.standDraft !== (this.savedNote?.text ?? '');
  }

  get hasSavedNote(): boolean {
    return !!this.savedNote;
  }

  saveNote(): void {
    if (!this.noteDirty || this.isSaving) return;
    const text = this.standDraft.trim();
    const base = copyNotes(this.entry.notes);

    // Clearing an existing note: drop it, restore on failure.
    if (!text) {
      if (this.savedNote) {
        const removed = this.savedNote;
        this.entry.notes = this.entry.notes.filter(n => n !== removed);
        this.savedNote = null;
        this.persistNote(
          base,
          () => { this.standDraft = ''; this.flashSaved(); },
          () => { this.entry.notes = [...this.entry.notes, removed]; this.savedNote = removed; },
        );
      } else {
        this.standDraft = '';
      }
      return;
    }

    // Editing an existing note: revert the text on failure.
    if (this.savedNote) {
      const note = this.savedNote;
      const previous = note.text;
      note.text = text;
      this.persistNote(
        base,
        () => { this.standDraft = text; this.flashSaved(); },
        () => { note.text = previous; },
      );
      return;
    }

    // New note: append optimistically, remove again on failure.
    // NP20-924: Der Autor wird nicht mehr vorbelegt — er kam aus dem Betreuer des Eintrags. Das
    // Backend stempelt ihn beim Speichern selbst (stampNewNotes) und liefert ihn in der Antwort.
    const note: NoteDTO = {id: null, date: this.today, author: '', text, isMeetingNote: true};
    this.savedNote = note;
    this.entry.notes = [...this.entry.notes, note];
    this.persistNote(
      base,
      () => { this.standDraft = text; this.flashSaved(); },
      () => { this.entry.notes = this.entry.notes.filter(n => n !== note); this.savedNote = null; },
    );
  }

  /** Newest first; the note composed this session is excluded until the next (re)load. */
  get history(): NoteDTO[] {
    return this.entry.notes
      .filter(n => n !== this.savedNote)
      .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
  }

  // --- date <-> moment ------------------------------------------------------

  toMoment(value: string | null): Moment | null {
    if (!value) return null;
    let m = this.momentCache.get(value);
    if (!m) {
      m = moment(value, 'YYYY-MM-DD');
      this.momentCache.set(value, m);
    }
    return m;
  }

  // --- persistence ----------------------------------------------------------

  private persistNote(base: NoteDTO[], onSuccess: () => void, onError: () => void): void {
    this.enqueue({kind: 'note', base, local: copyNotes(this.entry.notes), onSuccess, onError});
  }

  /**
   * NP20-1149: Frisch laden und nur die Bemerkungen zusammenfuehren, damit Aenderungen anderer am
   * Eintrag erhalten bleiben. Konflikt nur, wenn jemand dieselbe Bemerkung geaendert hat oder der
   * PUT trotzdem 409 liefert; dann kommt der frische Stand zurueck.
   */
  private mergeNote(job: NoteJob): Observable<PersistResult> {
    const id = this.entry.id;
    const reload = () => this.benchService.getEntry(id).pipe(map(updated => ({updated, conflict: true})));
    return this.benchService.getEntry(id).pipe(
      concatMap(fresh => notesConflict(job.base, job.local, fresh.notes) || this.todaysNoteAddedElsewhere(job, fresh.notes)
        ? of({updated: fresh, conflict: true})
        : this.benchService.updateEntry(id, withDatesNormalized(notesPayload(fresh, job.base, job.local))).pipe(
            map(updated => ({updated, conflict: false})),
            catchError((err: unknown) =>
              err instanceof HttpErrorResponse && err.status === 409 ? reload() : throwError(() => err)),
          )),
    );
  }

  /** Neue heutige Meeting-Notiz, waehrend jemand anderes schon eine angelegt hat: sonst entstuenden zwei. */
  private todaysNoteAddedElsewhere(job: NoteJob, fresh: NoteDTO[]): boolean {
    const isTodays = (n: NoteDTO) => n.isMeetingNote && n.date === this.today;
    if (!job.local.some(n => !n.id && isTodays(n))) return false;
    const baseIds = new Set(job.base.map(n => n.id).filter(id => !!id));
    return fresh.some(n => isTodays(n) && !baseIds.has(n.id));
  }

  /** Nur Bemerkungen und version uebernehmen; parallel getippte Inline-Werte bleiben stehen. */
  private adoptNotes(updated: BenchEntryDTO): void {
    this.entry.notes = updated.notes;
    this.entry.version = updated.version;
    this.savedNote = this.findTodaysNote();
  }

  private enqueue(job: PersistJob): void {
    this.inFlight++;
    this.cdr.markForCheck();
    this.persistQueue.next(job);
  }

  /**
   * Nur das gerade gespeicherte Feld uebernehmen — der uebrige Eintrag gehoert der
   * Elternkomponente (sie normalisiert etwa die Firma) und parallel getippter Text darf nicht
   * ueberschrieben werden. Der Hinweis meldet Erfolg nur, wenn der Server den Wert auch
   * uebernommen hat: fuer verknuepfte Eintraege liefert er bei "Verfuegbar ab" den Profilwert.
   */
  private adoptInlineResult(field: InlineField, updated: BenchEntryDTO, sent: BenchEntryPatch): void {
    const stored = emptyToNull(updated[field]);
    const requested = emptyToNull(sent[field]);
    this.serverState[field] = stored;

    if (emptyToNull(this.entry[field]) !== requested) return;

    this.entry[field] = stored;
    this.queuedState[field] = stored;
    this.momentCache.clear();
    if (stored === requested) this.flashInlineSaved(field);
  }

  private revertInline(field: InlineField, sent: BenchEntryPatch): void {
    if (emptyToNull(this.entry[field]) !== emptyToNull(sent[field])) return;

    this.entry[field] = this.serverState[field];
    this.queuedState[field] = this.serverState[field];
    this.momentCache.clear();
  }

  private captureServerState(): void {
    this.serverState = {
      availableFrom: emptyToNull(this.entry.availableFrom),
      nextStep: emptyToNull(this.entry.nextStep),
    };
    this.queuedState = {...this.serverState};
  }

  private flashSaved(): void {
    this.showSaved = true;
    this.cdr.markForCheck();
    if (this.savedTimeout !== null) clearTimeout(this.savedTimeout);
    this.savedTimeout = setTimeout(() => {
      this.showSaved = false;
      this.savedTimeout = null;
      this.cdr.markForCheck();
    }, SAVED_HINT_MS);
  }

  private flashInlineSaved(field: InlineField): void {
    this.savedField = field;
    this.cdr.markForCheck();
    if (this.inlineSavedTimeout !== null) clearTimeout(this.inlineSavedTimeout);
    this.inlineSavedTimeout = setTimeout(() => {
      this.savedField = null;
      this.inlineSavedTimeout = null;
      this.cdr.markForCheck();
    }, SAVED_HINT_MS);
  }
}
